/**
 * The interactive copilot's cards in the imports conversation (contract v1, 2026-09-26): the ask card under a brain
 * message that asks back, the proposal card under one that proposes a change (an Acquisition study's decisions have a
 * card of their own, AcquisitionChangeCard, P3c), and the starter questions above the
 * composer. What each shows is decided in lib/imports/copilot-blocks and copilot-proposals, and the container in
 * imports-copilot.tsx runs the actions; these are pure (rendered in tests without providers). The brain's words are
 * shown as text, never as markup.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, CheckCircle2, CircleSlash, HelpCircle, Loader2, MessageSquareQuote, Send, Sparkles } from "lucide-react";
import { Badge, type BadgeProps } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { cn } from "@/lib/ui/cn";
import {
  REJECT_REASON_MAX,
  type CopilotAnswerValues,
  type CopilotAskBlock,
  type CopilotAskInput,
  type CopilotCorrectionBlock,
  type CopilotDateRange,
  type CopilotPlantAnswerBlock,
  type CopilotProposalBlock,
  type CopilotProposalKind,
  type CopilotProposalStatus,
} from "@/lib/api/copilot-imports";
import {
  correctionKindWords,
  correctionRecorded,
  correctionRequest,
  correctionStateLine,
  plantAnswerOpen,
  plantAnswerRequest,
  plantAnswerStateLine,
} from "@/lib/copilot/corrections";
import {
  EMPTY_DRAFT,
  MAX_TEXT_ANSWER,
  amountText,
  checkAnswer,
  fieldLabel,
  withChoice,
  withInput,
  type AnswerCheck,
  type AskDraft,
} from "@/lib/imports/copilot-blocks";
import { PROPOSAL_KIND_LABEL, proposalKindLabel, type ProposalView } from "@/lib/imports/copilot-proposals";
import { CARD_TIME_UNKNOWN, CHANGE_STATE_WORDS, NOTHING_TO_APPLY, type ChangeLine } from "@/lib/acquisition/consultant";
import type { Refusal } from "@/lib/imports/copilot";

/** The server's code for a failure, kept small: for a report, never in place of the sentence (#843 review N3). */
export function ErrorCode({ code }: { code: string | null }) {
  if (!code) return null;
  return <code className="ml-1.5 font-mono text-[10px] text-on-surface-muted">{`(${code})`}</code>;
}

const CHIP =
  "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 " +
  "focus-visible:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50";
const CHIP_ON = "border-brand bg-brand text-brand-foreground";
const CHIP_OFF = "border-border bg-surface-muted text-on-surface hover:bg-surface-muted/70";
const FIELD = "h-8 text-xs";
const FIELD_LABEL = "text-xs text-on-surface-muted";

/** A number's bounds in words, under its field: "Between 1200 and 1600 °C". */
function boundsText(input: CopilotAskInput): string | null {
  const { min, max, unit } = input;
  if (min !== undefined && max !== undefined) return `Between ${min} and ${amountText(max, unit)}`;
  if (min !== undefined) return `At least ${amountText(min, unit)}`;
  if (max !== undefined) return `At most ${amountText(max, unit)}`;
  return null;
}

/** One of an ask's fields, each with its own label. */
function AskField({
  input,
  id,
  value,
  disabled,
  onInput,
}: {
  input: CopilotAskInput;
  id: string;
  value: string | CopilotDateRange | undefined;
  disabled: boolean;
  onInput: (v: string | CopilotDateRange) => void;
}) {
  const label = fieldLabel(input);
  const text = typeof value === "string" ? value : "";
  switch (input.kind) {
    case "date_range": {
      const r = typeof value === "object" && value !== null ? value : { from: "", to: "" };
      return (
        <fieldset className="flex flex-col gap-1" disabled={disabled}>
          <legend className={cn(FIELD_LABEL, "mb-1")}>{label}</legend>
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor={`${id}-from`} className={FIELD_LABEL}>From</label>
            <Input
              id={`${id}-from`}
              type="date"
              value={r.from}
              max={r.to || undefined}
              onChange={(e) => onInput({ ...r, from: e.target.value })}
              className={cn(FIELD, "w-auto")}
            />
            <label htmlFor={`${id}-to`} className={FIELD_LABEL}>To</label>
            <Input
              id={`${id}-to`}
              type="date"
              value={r.to}
              min={r.from || undefined}
              onChange={(e) => onInput({ ...r, to: e.target.value })}
              className={cn(FIELD, "w-auto")}
            />
          </div>
        </fieldset>
      );
    }
    case "number": {
      const hint = boundsText(input);
      return (
        <div className="flex flex-col gap-1">
          <label htmlFor={id} className={FIELD_LABEL}>{input.unit ? `${label} (${input.unit})` : label}</label>
          {/* Text, not type=number: the browser gives a number field's value as "" while what is typed is not a
              number, so the card could not say what is wrong (checkAnswer does). */}
          <Input
            id={id}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={text}
            disabled={disabled}
            aria-describedby={hint ? `${id}-hint` : undefined}
            onChange={(e) => onInput(e.target.value)}
            className={cn(FIELD, "w-40")}
          />
          {hint && <p id={`${id}-hint`} className="text-[11px] text-on-surface-muted">{hint}</p>}
        </div>
      );
    }
    case "select":
      return (
        <div className="flex flex-col gap-1">
          <label htmlFor={id} className={FIELD_LABEL}>{label}</label>
          <select
            id={id}
            value={text}
            disabled={disabled}
            onChange={(e) => onInput(e.target.value)}
            className="h-8 w-fit max-w-full rounded-md border border-border bg-surface px-2 text-xs text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          >
            <option value="">Choose…</option>
            {(input.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      );
    case "date":
      return (
        <div className="flex flex-col gap-1">
          <label htmlFor={id} className={FIELD_LABEL}>{label}</label>
          <Input
            id={id}
            type="date"
            value={text}
            disabled={disabled}
            onChange={(e) => onInput(e.target.value)}
            className={cn(FIELD, "w-auto")}
          />
        </div>
      );
    default:
      return (
        <div className="flex flex-col gap-1">
          <label htmlFor={id} className={FIELD_LABEL}>{label}</label>
          <Input
            id={id}
            type="text"
            maxLength={MAX_TEXT_ANSWER}
            value={text}
            disabled={disabled}
            onChange={(e) => onInput(e.target.value)}
            className={FIELD}
          />
        </div>
      );
  }
}

/**
 * The ask card, as its state stands (AskCard holds the state). The question, the choices as buttons that say whether
 * they are picked (one, or several when the ask allows), the fields, and Send, held until there is an answer (why,
 * once something is picked or typed). Once answered it says what was chosen, and offers nothing to change. A person
 * who may not ask reads the question only.
 */
export function AskCardView({
  ask,
  idPrefix,
  draft,
  check,
  touched,
  canAnswer,
  sending,
  busy,
  error,
  answered,
  onChoice,
  onInput,
  onSend,
}: {
  ask: CopilotAskBlock;
  /** Unique on the page: the ids of the question and the fields start with it. */
  idPrefix: string;
  draft: AskDraft;
  check: AnswerCheck;
  /** Something was picked or typed: the reason Send waits shows. */
  touched: boolean;
  canAnswer: boolean;
  /** This card's answer goes up. */
  sending: boolean;
  /** A message goes up from this page (this answer or another): Send waits. */
  busy: boolean;
  /** Why the server refused the answer. */
  error: Refusal | null;
  /** The answer's words once the ask is answered, else null. */
  answered: string | null;
  onChoice: (value: string) => void;
  onInput: (name: string, value: string | CopilotDateRange) => void;
  onSend: (values: CopilotAnswerValues) => void;
}) {
  const qid = `${idPrefix}-q`;
  if (answered !== null) {
    return (
      <div className="flex flex-col gap-1 rounded-md border border-border bg-surface p-3">
        <p className="flex items-start gap-1.5 text-sm font-medium text-on-surface">
          <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-success" aria-hidden />
          <span className="min-w-0 break-words">{ask.question}</span>
        </p>
        <p className="break-words text-xs text-on-surface-muted">
          <span className="font-medium">Answered: </span>
          <span className="text-on-surface">{answered}</span>
        </p>
      </div>
    );
  }
  const locked = !canAnswer || sending;
  const problem = touched && !check.ok ? check.problem : null;
  const problemId = `${idPrefix}-problem`;
  return (
    <form
      aria-labelledby={qid}
      className="flex flex-col gap-2.5 rounded-md border border-border bg-surface p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (check.ok && canAnswer && !busy) onSend(check.values);
      }}
    >
      <p id={qid} className="flex items-start gap-1.5 text-sm font-medium text-on-surface">
        <HelpCircle className="mt-0.5 size-3.5 shrink-0 text-brand" aria-hidden />
        <span className="min-w-0 break-words">{ask.question}</span>
      </p>
      {ask.choices.length > 0 && (
        <div role="group" aria-labelledby={qid} className="flex flex-wrap gap-1.5">
          {ask.choices.map((c) => {
            const on = draft.choice.includes(c.value);
            return (
              <button
                key={c.value}
                type="button"
                aria-pressed={on}
                disabled={locked}
                onClick={() => onChoice(c.value)}
                className={cn(CHIP, on ? CHIP_ON : CHIP_OFF)}
              >
                {on && <Check className="size-3" aria-hidden />}
                {c.label}
              </button>
            );
          })}
        </div>
      )}
      {ask.inputs.map((input) => (
        <AskField
          key={input.name}
          input={input}
          id={`${idPrefix}-${input.name}`}
          value={draft.inputs[input.name]}
          disabled={locked}
          onInput={(v) => onInput(input.name, v)}
        />
      ))}
      {canAnswer && (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" size="sm" disabled={!check.ok || busy} aria-describedby={problem ? problemId : undefined}>
            {sending ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
            {sending ? "Sending" : "Send"}
          </Button>
          {problem && <p id={problemId} className="text-xs text-on-surface-muted">{problem}</p>}
        </div>
      )}
      {error && (
        <p role="alert" className="break-words text-xs text-danger">
          {error.text}
          <ErrorCode code={error.code} />
        </p>
      )}
    </form>
  );
}

/** The ask card with its own state: what is picked and typed, from blank. Send gives the checked values. */
export function AskCard({
  ask,
  idPrefix,
  answered,
  canAnswer,
  sending,
  busy,
  error,
  onAnswer,
}: {
  ask: CopilotAskBlock;
  idPrefix: string;
  answered: string | null;
  canAnswer: boolean;
  sending: boolean;
  busy: boolean;
  error: Refusal | null;
  onAnswer: (values: CopilotAnswerValues) => void;
}) {
  const [draft, setDraft] = useState<AskDraft>(EMPTY_DRAFT);
  const [touched, setTouched] = useState(false);
  return (
    <AskCardView
      ask={ask}
      idPrefix={idPrefix}
      draft={draft}
      check={checkAnswer(ask, draft)}
      touched={touched}
      canAnswer={canAnswer}
      sending={sending}
      busy={busy}
      error={error}
      answered={answered}
      onChoice={(v) => {
        setDraft((d) => withChoice(ask, d, v));
        setTouched(true);
      }}
      onInput={(name, v) => {
        setDraft((d) => withInput(d, name, v));
        setTouched(true);
      }}
      onSend={onAnswer}
    />
  );
}

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

const KIND_BADGE: Record<CopilotProposalKind, BadgeVariant> = {
  correlation_group: "info",
  alarm_rule: "warning",
  report: "default",
  dashboard_widget: "secondary",
  process_window: "info",
  scrap_target: "warning",
  acquisition_change: "info",
};
const STATE_BADGE: Record<CopilotProposalStatus, BadgeVariant> = {
  proposed: "outline",
  applied: "success",
  failed: "danger",
  rejected: "secondary",
};
const STATE_TEXT: Record<CopilotProposalStatus, string> = {
  proposed: "Proposed",
  applied: "Applied",
  failed: "Failed",
  rejected: "Rejected",
};
const LINK_TEXT: Record<CopilotProposalKind, string> = {
  correlation_group: "Open correlation groups",
  alarm_rule: "Open alarm rules",
  report: "Open the report",
  dashboard_widget: "Open the dashboard",
  process_window: "Open the plant's limits",
  scrap_target: "Open the plant's limits",
  acquisition_change: "Open the study",
};

/**
 * Why the person declines a proposal (L2), asked once they press Reject: a line of their words, optional, and Reject to
 * record it, or Cancel. No hooks: ProposalCard holds the words.
 */
export function RejectWhy({
  idPrefix,
  reason,
  busy,
  onChange,
  onConfirm,
  onCancel,
}: {
  idPrefix: string;
  reason: string;
  busy: boolean;
  onChange: (reason: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div data-reject-why="" className="flex flex-col gap-1.5">
      <label htmlFor={`${idPrefix}-why`} className={FIELD_LABEL}>
        Why not? (optional: the consultant reads it)
      </label>
      <Input
        id={`${idPrefix}-why`}
        className={FIELD}
        value={reason}
        maxLength={REJECT_REASON_MAX}
        onChange={(e) => onChange(e.target.value)}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onConfirm} disabled={busy}>
          {busy && <Loader2 className="size-3.5 animate-spin" />}
          {busy ? "Rejecting" : "Reject"}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * The proposal card: its kind and state, the brain's title and summary, the spec in words, and what can be done
 * (proposalView): Approve and Reject (which asks why, optionally, L2); Try again after a failure, with its reason; Save
 * for a change made but not yet recorded; a link to what an applied proposal made; a rejected one's reason. Approve is
 * held with the reason when the person's role may not make the change.
 */
export function ProposalCard({
  block,
  view,
  specText,
  onApprove,
  onReject,
  onSave,
}: {
  block: CopilotProposalBlock;
  view: ProposalView;
  specText: string;
  onApprove: () => void;
  onReject: (reason: string) => void;
  onSave: () => void;
}) {
  const [why, setWhy] = useState<string | null>(null);
  const base = `proposal-${block.proposal_id ?? block.id}`;
  const noteId = `${base}-note`;
  const buttons = view.approve || view.reject || view.save;
  const declined = view.state === "rejected" ? block.result?.reason?.trim() : undefined;
  return (
    <section aria-labelledby={`${base}-title`} className="flex flex-col gap-2 rounded-md border border-border bg-surface p-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <Sparkles className="size-3.5 text-brand" aria-hidden />
        <Badge variant={KIND_BADGE[block.kind]}>{proposalKindLabel(block)}</Badge>
        <Badge variant={STATE_BADGE[view.state]}>{STATE_TEXT[view.state]}</Badge>
      </div>
      <p id={`${base}-title`} className="break-words text-sm font-semibold text-on-surface">{block.title}</p>
      {block.summary && <p className="whitespace-pre-wrap break-words text-xs text-on-surface-muted">{block.summary}</p>}
      {specText && <p className="break-words text-xs font-medium text-on-surface">{specText}</p>}
      {view.error && (
        <p role="alert" className="break-words text-xs text-danger">
          {view.error.text}
          <ErrorCode code={view.error.code} />
        </p>
      )}
      {view.link && (
        <Link to={view.link} className="w-fit text-xs font-medium text-brand underline">
          {LINK_TEXT[block.kind]}
        </Link>
      )}
      {declined && <p className="break-words text-xs text-on-surface-muted">{`Declined: ${declined}`}</p>}
      {view.reject && why !== null && !view.reject.disabled && (
        <RejectWhy
          idPrefix={base}
          reason={why}
          busy={view.reject.busy}
          onChange={setWhy}
          onConfirm={() => {
            onReject(why.trim());
            setWhy(null);
          }}
          onCancel={() => setWhy(null)}
        />
      )}
      {buttons && (why === null || !view.reject || view.reject.disabled) && (
        <div className="flex flex-wrap items-center gap-2">
          {view.approve && (
            <Button
              type="button"
              size="sm"
              onClick={onApprove}
              disabled={view.approve.disabled}
              aria-describedby={view.note ? noteId : undefined}
            >
              {view.approve.busy && <Loader2 className="size-3.5 animate-spin" />}
              {view.approve.busy ? "Applying" : view.approve.label}
            </Button>
          )}
          {view.reject && (
            <Button type="button" size="sm" variant="outline" onClick={() => setWhy("")} disabled={view.reject.disabled}>
              {view.reject.busy && <Loader2 className="size-3.5 animate-spin" />}
              {view.reject.busy ? "Rejecting" : view.reject.label}
            </Button>
          )}
          {view.save && (
            <Button type="button" size="sm" onClick={onSave} disabled={view.save.disabled}>
              {view.save.busy && <Loader2 className="size-3.5 animate-spin" />}
              {view.save.busy ? "Saving" : view.save.label}
            </Button>
          )}
        </div>
      )}
      {view.note && <p id={noteId} className="text-xs text-on-surface-muted">{view.note}</p>}
    </section>
  );
}

/**
 * An Acquisition study's decisions the consultant proposes (acquisition_change, Acquisition P3c): the brain's title and
 * summary, then each change (the point, accept, edit or reject, what an edit sets, and why) and what becomes of it when
 * applied, once the study is read: a change whose row was decided otherwise since the consultant read the study, or is already so,
 * says it is skipped. An org admin applies the card ("Apply these decisions": their browser saves the decisions as
 * theirs, through the study's own route) or dismisses it; anyone else reads it. It names the study it is for, once the
 * study is read ("For the study of <source name>"). Applied, it links to the study and keeps saying how many rows it
 * applied and skipped (the outcome's summary).
 */
export function AcquisitionChangeCard({
  block,
  view,
  studyName,
  lines,
  canApply,
  onApply,
  onDismiss,
  onSave,
}: {
  block: CopilotProposalBlock;
  view: ProposalView;
  /** The study's source name, once the study is read: what the card is for. */
  studyName?: string | null;
  /** Each change as the card lists it (changeLines), its state null while the study is read. */
  lines: ChangeLine[];
  /** An org admin, on a thread that can be decided: the buttons show. */
  canApply: boolean;
  onApply: () => void;
  onDismiss: () => void;
  onSave: () => void;
}) {
  const base = `proposal-${block.proposal_id ?? block.id}`;
  const noteId = `${base}-note`;
  // Every change skipped (the study read, none left to apply): Apply has nothing to send.
  const nothing = lines.length > 0 && lines.every((l) => l.state !== null && l.state !== "apply");
  // The card's time could not be read: nothing can be applied from it, and it says so rather than "decided since".
  const timeUnknown = lines.some((l) => l.state === "time_unknown");
  const open = !view.final;
  const stateText = view.state === "rejected" ? "Dismissed" : STATE_TEXT[view.state];
  const declined = view.state === "rejected" ? block.result?.reason?.trim() : undefined;
  // What applying did and skipped, as the outcome recorded it: the skip words above show only while the card is open.
  const done = view.state === "applied" && view.final ? block.result?.summary?.trim() : undefined;
  return (
    <section
      aria-labelledby={`${base}-title`}
      data-acquisition-change=""
      className="flex flex-col gap-2 rounded-md border border-border bg-surface p-3"
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <Sparkles className="size-3.5 text-brand" aria-hidden />
        <Badge variant={KIND_BADGE.acquisition_change}>{PROPOSAL_KIND_LABEL.acquisition_change}</Badge>
        <Badge variant={STATE_BADGE[view.state]}>{stateText}</Badge>
      </div>
      <p id={`${base}-title`} className="break-words text-sm font-semibold text-on-surface">{block.title}</p>
      {block.summary && <p className="whitespace-pre-wrap break-words text-xs text-on-surface-muted">{block.summary}</p>}
      {studyName && (
        <p data-change-study="" className="break-words text-xs text-on-surface-muted">
          {"For the study of "}
          <span className="font-medium text-on-surface">{studyName}</span>
        </p>
      )}
      <ol aria-label="The changes" className="flex flex-col gap-1.5">
        {lines.map((l) => {
          const skipped = l.state !== null && l.state !== "apply";
          return (
            <li
              key={l.key}
              data-change-state={l.state ?? "unread"}
              className={cn("flex flex-col gap-0.5 rounded-md border border-border px-2 py-1.5 text-xs", skipped && open && "opacity-70")}
            >
              <p className="break-words text-on-surface">
                <span className="font-medium">{l.point}</span>
                {`: ${l.decision}`}
                {l.what && <span className="text-on-surface-muted">{`, ${l.what}`}</span>}
              </p>
              {l.reason && <p className="break-words text-on-surface-muted">{`Why: ${l.reason}`}</p>}
              {skipped && open && l.state && <p className="text-warning">{CHANGE_STATE_WORDS[l.state]}</p>}
            </li>
          );
        })}
      </ol>
      {view.error && (
        <p role="alert" className="break-words text-xs text-danger">
          {view.error.text}
          <ErrorCode code={view.error.code} />
        </p>
      )}
      {view.link && (
        <Link to={view.link} className="w-fit text-xs font-medium text-brand underline">
          {LINK_TEXT.acquisition_change}
        </Link>
      )}
      {done && (
        <p data-change-applied="" className="break-words text-xs text-on-surface">
          {done}
        </p>
      )}
      {declined && <p className="break-words text-xs text-on-surface-muted">{`Dismissed: ${declined}`}</p>}
      {open && canApply && (view.approve || view.reject || view.save) && (
        <div className="flex flex-wrap items-center gap-2">
          {view.approve && (
            <Button
              type="button"
              size="sm"
              onClick={onApply}
              disabled={view.approve.disabled || nothing}
              aria-describedby={view.note || nothing ? noteId : undefined}
            >
              {view.approve.busy && <Loader2 className="size-3.5 animate-spin" />}
              {view.approve.busy ? "Applying" : view.approve.label === "Try again" ? "Try again" : "Apply these decisions"}
            </Button>
          )}
          {view.reject && (
            <Button type="button" size="sm" variant="outline" onClick={onDismiss} disabled={view.reject.disabled}>
              {view.reject.busy && <Loader2 className="size-3.5 animate-spin" />}
              {view.reject.busy ? "Dismissing" : "Dismiss"}
            </Button>
          )}
          {view.save && (
            <Button type="button" size="sm" onClick={onSave} disabled={view.save.disabled}>
              {view.save.busy && <Loader2 className="size-3.5 animate-spin" />}
              {view.save.busy ? "Saving" : view.save.label}
            </Button>
          )}
        </div>
      )}
      {open && canApply && (view.note || nothing) && (
        <p id={noteId} className="text-xs text-on-surface-muted">{view.note ?? (timeUnknown ? CARD_TIME_UNKNOWN : NOTHING_TO_APPLY)}</p>
      )}
      {open && !canApply && (
        <p data-change-read-only="" className="text-xs text-on-surface-muted">An org admin applies these decisions.</p>
      )}
    </section>
  );
}

const TEXTAREA =
  "w-full rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs text-on-surface focus:border-brand " +
  "focus:outline-none disabled:opacity-60";

/**
 * A correction card (L2): a recommendation the person said is wrong, or does not apply, in the platform's words; their
 * reason, quoted from what they wrote, which they may edit; and "Record this correction", which records it as theirs
 * (the container posts it, via this thread). The consultant records nothing. Recorded, it says so; a person whose rank
 * gives no verdicts (knowledge.respond) is told who does. A thread shown read-only (readOnly) shows the recommendation,
 * the person's reason as text and the key's state as it is now: no editor, no button, and nothing said of a role.
 */
export function CorrectionCard({
  block,
  threadId,
  readOnly = false,
  canRecord,
  busy,
  error,
  onRecord,
}: {
  block: CopilotCorrectionBlock;
  /** The thread the card is recorded from; null where the thread is shown read-only. */
  threadId: string | null;
  readOnly?: boolean;
  canRecord: boolean;
  busy: boolean;
  error: string | null;
  onRecord: (reason: string) => void;
}) {
  const [reason, setReason] = useState(block.reason);
  const recorded = correctionRecorded(block, threadId);
  const req = threadId === null ? null : correctionRequest(block, threadId, reason);
  const state = correctionStateLine(block, threadId);
  const base = `correction-${block.id}`;
  return (
    <section
      aria-labelledby={`${base}-title`}
      data-correction={block.kind}
      className="flex flex-col gap-2 rounded-md border border-border bg-surface p-3"
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <CircleSlash className="size-3.5 text-warning" aria-hidden />
        <Badge variant="warning">Correction</Badge>
        <Badge variant={recorded ? "success" : "outline"}>{recorded ? "Recorded" : correctionKindWords(block.kind)}</Badge>
      </div>
      <p id={`${base}-title`} className="break-words text-sm font-semibold text-on-surface">
        {block.text?.trim() || block.key}
      </p>
      {recorded ? (
        <p className="whitespace-pre-wrap break-words text-xs text-on-surface-muted">{state}</p>
      ) : readOnly ? (
        <>
          <p className="whitespace-pre-wrap break-words text-xs text-on-surface-muted">Reason: {block.reason}</p>
          {state && <p className="whitespace-pre-wrap break-words text-xs text-on-surface-muted">{state}</p>}
        </>
      ) : (
        <>
          <label htmlFor={`${base}-why`} className={FIELD_LABEL}>
            Your reason (your words, as you wrote them: change them if you like)
          </label>
          <textarea
            id={`${base}-why`}
            rows={2}
            className={TEXTAREA}
            value={reason}
            disabled={busy || !canRecord}
            onChange={(e) => setReason(e.target.value)}
          />
          {state && <p className="text-xs text-on-surface-muted">{state}</p>}
          {canRecord ? (
            <Button type="button" size="sm" className="w-fit" onClick={() => req?.body && onRecord(reason.trim())} disabled={busy || !req?.body}>
              {busy && <Loader2 className="size-3.5 animate-spin" />}
              {busy ? "Recording" : "Record this correction"}
            </Button>
          ) : (
            <p className="text-xs text-on-surface-muted">A member of the plant records a correction: your role cannot.</p>
          )}
          {req?.problem && <p className="text-xs text-on-surface-muted">{req.problem}</p>}
        </>
      )}
      {error && (
        <p role="alert" className="break-words text-xs text-danger">
          {error}
        </p>
      )}
    </section>
  );
}

/**
 * A Record-as-answer card (L2; the async copilot's slice 5): the open plant question the person's words answer, by its
 * topic, their words, which they may edit, and "Record as answer", which records it as theirs on the question (the
 * container posts it). Once the question is answered, closed or withdrawn, it says so and offers nothing; a thread shown
 * read-only (readOnly) shows the person's words as text, with no editor, no button and nothing said of a role.
 */
export function PlantAnswerCard({
  block,
  readOnly = false,
  canRecord,
  busy,
  error,
  onRecord,
}: {
  block: CopilotPlantAnswerBlock;
  readOnly?: boolean;
  canRecord: boolean;
  busy: boolean;
  error: string | null;
  onRecord: (text: string) => void;
}) {
  const [text, setText] = useState(block.text);
  const open = plantAnswerOpen(block);
  const req = plantAnswerRequest(text);
  const state = plantAnswerStateLine(block);
  const base = `plant-answer-${block.id}`;
  return (
    <section
      aria-labelledby={`${base}-title`}
      data-plant-answer={block.status ?? "asked"}
      className="flex flex-col gap-2 rounded-md border border-border bg-surface p-3"
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <MessageSquareQuote className="size-3.5 text-brand" aria-hidden />
        <Badge variant="info">Plant question</Badge>
        {!open && <Badge variant="success">Answered</Badge>}
      </div>
      <p id={`${base}-title`} className="break-words text-sm font-semibold text-on-surface">
        {block.topic?.trim() || "An open plant question"}
      </p>
      {open && readOnly ? (
        <p className="whitespace-pre-wrap break-words text-xs text-on-surface-muted">Answer: {block.text}</p>
      ) : open ? (
        <>
          <label htmlFor={`${base}-text`} className={FIELD_LABEL}>
            Your answer (your words, as you wrote them: change them if you like)
          </label>
          <textarea
            id={`${base}-text`}
            rows={3}
            className={TEXTAREA}
            value={text}
            disabled={busy || !canRecord}
            onChange={(e) => setText(e.target.value)}
          />
          {canRecord ? (
            <Button type="button" size="sm" className="w-fit" onClick={() => req.body && onRecord(req.body.text)} disabled={busy || !req.body}>
              {busy && <Loader2 className="size-3.5 animate-spin" />}
              {busy ? "Recording" : "Record as answer"}
            </Button>
          ) : (
            <p className="text-xs text-on-surface-muted">A member of the plant records an answer: your role cannot.</p>
          )}
          {req.problem && <p className="text-xs text-on-surface-muted">{req.problem}</p>}
        </>
      ) : (
        <p className="whitespace-pre-wrap break-words text-xs text-on-surface-muted">{state}</p>
      )}
      {error && (
        <p role="alert" className="break-words text-xs text-danger">
          {error}
        </p>
      )}
    </section>
  );
}

/** Questions to start with, above the composer: in full for an empty thread, as a compact row otherwise. */
export function StarterChips({
  starters,
  compact,
  disabled,
  onPick,
}: {
  starters: readonly string[];
  compact: boolean;
  disabled: boolean;
  onPick: (text: string) => void;
}) {
  return (
    <div className={cn("flex gap-2", compact ? "flex-row flex-wrap items-center" : "flex-col")}>
      <p id="copilot-starters-label" className="text-xs text-on-surface-muted">{compact ? "Try:" : "Try asking"}</p>
      <div role="group" aria-labelledby="copilot-starters-label" className="flex flex-wrap gap-1.5">
        {starters.map((s) => (
          <button
            key={s}
            type="button"
            disabled={disabled}
            onClick={() => onPick(s)}
            className={cn(CHIP, CHIP_OFF, compact && "px-2.5 py-0.5 text-[11px]")}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
